/**
 * The refine review page (`/assets/:assetId/refine/:extractionId`): while the refinement runs, the waiting card (its
 * status asked every 3 s, a hidden status line saying "Refining…" then "Ready"); once it succeeded, the review; else
 * why not (failed with the reason and **Try again**, gone, or no changes proposed).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { useAsset, useExtractionProposal, useExtractionStatus } from '@/api/queries';
import type { AssetOut } from '@/api/types/assets';
import { useSession } from '@/session/SessionContext';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { WaitingCard } from '../files/WaitingCard';
import { RefineAssetDialog } from './RefineAssetDialog';
import { keptInstruction } from './refineDraft';
import { RefineReview } from './RefineReview';
import { NoChanges, RefinementFailed, RefinementGone } from './RefineStates';

const gone = (error: unknown) => error instanceof ApiError && error.status === 404;

type Retry = ((instruction: string) => void) | undefined;

function Ready({ asset, extractionId, onRetry }: { asset: AssetOut; extractionId: string; onRetry: Retry }) {
  const proposal = useExtractionProposal(extractionId, true);
  if (proposal.error) {
    return gone(proposal.error) ? <RefinementGone assetId={asset.id} /> : <LoadFailure error={proposal.error} onRetry={() => void proposal.refetch()} />;
  }
  const data = proposal.data;
  if (!data) return <p className="hint">Opening the refinement…</p>;
  if (data.mode !== 'refine' || data.asset_id !== asset.id) return <RefinementGone assetId={asset.id} />;
  if (data.changes.length === 0) return <NoChanges proposal={data} onRetry={onRetry && (() => onRetry(data.instruction))} />;
  return <RefineReview asset={asset} proposal={data} />;
}

/** Succeeded: the review needs the asset as it is now (its text and revision). */
function Succeeded({ assetId, extractionId, onRetry }: { assetId: string; extractionId: string; onRetry: Retry }) {
  const asset = useAsset(assetId);
  if (asset.error) return gone(asset.error) ? <RefinementGone assetId={assetId} /> : <LoadFailure error={asset.error} onRetry={() => void asset.refetch()} />;
  if (!asset.data) return <LoadingCard label="Loading the asset" />;
  return <Ready asset={asset.data} extractionId={extractionId} onRetry={onRetry} />;
}

function Body({ assetId, extractionId, onRetry }: { assetId: string; extractionId: string; onRetry: Retry }) {
  const { user } = useSession();
  const status = useExtractionStatus(extractionId);
  if (status.error) {
    return gone(status.error) ? <RefinementGone assetId={assetId} /> : <LoadFailure error={status.error} onRetry={() => void status.refetch()} />;
  }
  const current = status.data?.status ?? 'queued';
  if (current === 'failed') {
    const retry = onRetry && (() => onRetry(keptInstruction(user?.id ?? 'anonymous', extractionId)));
    return <RefinementFailed assetId={assetId} reason={status.data?.error ?? null} onRetry={retry} />;
  }
  if (current !== 'succeeded') {
    return <WaitingCard live={false} text="The AI is reading the asset and your instruction, and proposing changes. This takes a minute or two." hint="You can leave this page: the asset page links back here while the refinement is kept (7 days)." />;
  }
  return <Succeeded assetId={assetId} extractionId={extractionId} onRetry={onRetry} />;
}

/** What the hidden status line says: announced as the refinement moves on. */
function statusLine(status: string | undefined, failed: boolean): string {
  if (failed || status === 'failed') return '';
  return status === 'succeeded' ? 'Ready' : 'Refining…';
}

function RefineView({ assetId, extractionId }: { assetId: string; extractionId: string }) {
  const { can } = useSession();
  const asset = useAsset(assetId);
  const status = useExtractionStatus(extractionId);
  const [retry, setRetry] = useState<string | null>(null);
  const data = asset.data;
  const canRetry = data !== undefined && can('asset.manage') && data.status !== 'archived';
  return (
    <>
      <PageHead title={data ? `Refine ${data.name}` : 'Refine'} subtitle="Proposed by AI. Nothing changes until you apply." back={{ to: `/assets/${assetId}`, name: data?.name ?? 'the asset' }} />
      <p className="sr-only" role="status">{statusLine(status.data?.status, status.error !== null)}</p>
      <Body assetId={assetId} extractionId={extractionId} onRetry={canRetry ? setRetry : undefined} />
      {retry !== null && data && <RefineAssetDialog asset={data} initial={retry} onClose={() => setRetry(null)} />}
    </>
  );
}

export function RefinePage() {
  const { assetId = '', extractionId = '' } = useParams();
  // A new refinement (Try again) is a new page: its own status, draft and dialog state.
  return <RefineView key={extractionId} assetId={assetId} extractionId={extractionId} />;
}
